import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  FileSignature,
  Plus,
  Search,
  Download,
  Upload,
  Edit3,
  Trash2,
  AlertTriangle,
  CalendarClock,
  Building2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

import {
  listContracts,
  getContractsStats,
  createContract,
  updateContract,
  deleteContract,
  contractDownloadUrl,
  listFacilityLeads,
  listLeads,
  CONTRACT_STATUSES,
} from "@/lib/api";

const STATUS_BY_VALUE = Object.fromEntries(CONTRACT_STATUSES.map((s) => [s.value, s]));

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00");
    const ms = d.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const ExpiryBadge = ({ endDate, status }) => {
  if (!endDate || status !== "active") return null;
  const d = daysUntil(endDate);
  if (d === null) return null;
  if (d < 0)
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
            style={{ background: "rgba(155,28,28,0.10)", color: "#9B1C1C" }}
            data-testid="contract-expired-badge">
        <AlertTriangle className="h-3 w-3" /> Expired
      </span>
    );
  if (d <= 30)
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
            style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
            data-testid="contract-expiring-badge">
        <CalendarClock className="h-3 w-3" /> {d}d left
      </span>
    );
  return null;
};

const StatusPill = ({ status }) => {
  const meta = STATUS_BY_VALUE[status] || STATUS_BY_VALUE.draft;
  return (
    <span
      className="inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
      style={{ background: meta.bg, color: meta.color }}
    >
      {meta.label}
    </span>
  );
};

const ContractFormDialog = ({ open, initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("draft");
  const [linkType, setLinkType] = useState("none");
  const [linkId, setLinkId] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkOptions, setLinkOptions] = useState({ facility: [], consumer: [] });
  const [loadingLinks, setLoadingLinks] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setStatus(initial?.status || "draft");
    setLinkType(initial?.link_type || "none");
    setLinkId(initial?.link_id || "");
    setLinkLabel(initial?.link_label || "");
    setStartDate(initial?.start_date || "");
    setEndDate(initial?.end_date || "");
    setValue(initial?.value != null ? String(initial.value) : "");
    setNotes(initial?.notes || "");
    setFile(null);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoadingLinks(true);
      try {
        const [fac, con] = await Promise.all([listFacilityLeads(), listLeads()]);
        if (!cancelled) setLinkOptions({ facility: fac || [], consumer: con || [] });
      } catch {
        // non-fatal
      } finally {
        if (!cancelled) setLoadingLinks(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const linkCandidates =
    linkType === "facility"
      ? linkOptions.facility.map((f) => ({ id: f.id, label: `${f.facility_name} · ${f.contact_person}` }))
      : linkType === "consumer"
      ? linkOptions.consumer.map((c) => ({ id: c.id, label: `${c.full_name} · ${c.email}` }))
      : [];

  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateContract(initial.id, {
          title: title.trim(),
          status,
          link_type: linkType === "none" ? null : linkType,
          link_id: linkId || null,
          link_label: linkLabel || null,
          start_date: startDate || null,
          end_date: endDate || null,
          value: value ? Number(value) : null,
          notes: notes || null,
        });
        toast.success("Contract updated.");
      } else {
        const fd = new FormData();
        fd.append("title", title.trim());
        fd.append("status", status);
        if (linkType !== "none") {
          fd.append("link_type", linkType);
          if (linkId) fd.append("link_id", linkId);
          if (linkLabel) fd.append("link_label", linkLabel);
        }
        if (startDate) fd.append("start_date", startDate);
        if (endDate) fd.append("end_date", endDate);
        if (value) fd.append("value", value);
        if (notes) fd.append("notes", notes);
        if (file) fd.append("file", file);
        await createContract(fd);
        toast.success("Contract created.");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save contract.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="contract-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit contract" : "New contract"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update contract metadata. The attached file is preserved."
              : "Create a contract record. Optionally attach a signed PDF (max 25 MB)."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Riverside Clinic – Phlebotomy SLA" data-testid="contract-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="contract-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTRACT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value (USD)</Label>
              <Input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0.00" data-testid="contract-value" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="contract-start" />
            </div>
            <div>
              <Label>End date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="contract-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Linked to</Label>
              <Select value={linkType} onValueChange={(v) => { setLinkType(v); setLinkId(""); setLinkLabel(""); }}>
                <SelectTrigger data-testid="contract-link-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No link</SelectItem>
                  <SelectItem value="facility">Facility</SelectItem>
                  <SelectItem value="consumer">Consumer client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{linkType === "none" ? "Record" : "Pick record"}</Label>
              <Select
                value={linkId || "_none"}
                onValueChange={(v) => {
                  if (v === "_none") { setLinkId(""); setLinkLabel(""); return; }
                  const item = linkCandidates.find((c) => c.id === v);
                  setLinkId(v); setLinkLabel(item?.label || "");
                }}
                disabled={linkType === "none" || loadingLinks}
              >
                <SelectTrigger data-testid="contract-link-id"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— none —</SelectItem>
                  {linkCandidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="contract-notes" placeholder="Renewal cadence, signers, etc." />
          </div>
          {!isEdit && (
            <div>
              <Label>Attach signed PDF (optional, max 25 MB)</Label>
              <Input
                type="file"
                accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="contract-file"
              />
              {file && (
                <p className="text-xs mt-1.5" style={{ color: "#64748B" }}>
                  {file.name} · {Math.round(file.size / 1024)} KB
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contract-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="contract-save"
                  style={{ background: "#0B2545", color: "#FFFFFF" }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            {isEdit ? "Save changes" : "Create contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ContractsPanel({ onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([listContracts(), getContractsStats()]);
      setRows(list || []);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load contracts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (r) =>
          (r.title || "").toLowerCase().includes(q) ||
          (r.link_label || "").toLowerCase().includes(q) ||
          (r.notes || "").toLowerCase().includes(q) ||
          (r.filename || "").toLowerCase().includes(q),
      );
    }
    return out;
  }, [rows, statusFilter, search]);

  const onDelete = async (id) => {
    if (!window.confirm("Permanently delete this contract?")) return;
    try {
      await deleteContract(id);
      toast.success("Contract deleted.");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete.");
    }
  };

  return (
    <div className="space-y-6" data-testid="contracts-panel">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>
            Contracts
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>
            Contracts
          </h1>
          <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
            Track every contract — draft, pending, active, expired.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
                style={{ background: "#0B2545", color: "#FFFFFF" }}
                data-testid="contracts-new">
          <Plus className="h-4 w-4 mr-2" /> New contract
        </Button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="contracts-stats">
        <StatCard label="Total" value={stats?.total ?? 0} />
        <StatCard label="Active" value={stats?.by_status?.active ?? 0} accent="#0F6D5D" />
        <StatCard label="Expiring (30d)" value={stats?.expiring_within_30d ?? 0} accent="#7A5E12" />
        <StatCard label="Expired (still active)" value={stats?.expired_active ?? 0} accent="#9B1C1C" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
          <Input
            placeholder="Search contracts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="contracts-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label={`All (${rows.length})`} testid="filter-all" />
          {CONTRACT_STATUSES.map((s) => (
            <Chip
              key={s.value}
              active={statusFilter === s.value}
              onClick={() => setStatusFilter(s.value)}
              label={`${s.label} (${stats?.by_status?.[s.value] ?? 0})`}
              testid={`filter-${s.value}`}
            />
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed py-16 text-center" style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
          <FileSignature className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
          <p className="font-medium" style={{ color: "#0B2545" }}>No contracts yet</p>
          <p className="text-sm mt-1">Click <span className="font-medium">New contract</span> to add your first.</p>
        </div>
      ) : (
        <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "#F8FAFC" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-medium" style={{ color: "#64748B" }}>Title</th>
                <th className="px-4 py-3 font-medium" style={{ color: "#64748B" }}>Linked to</th>
                <th className="px-4 py-3 font-medium" style={{ color: "#64748B" }}>Status</th>
                <th className="px-4 py-3 font-medium" style={{ color: "#64748B" }}>Term</th>
                <th className="px-4 py-3 font-medium text-right" style={{ color: "#64748B" }}>Value</th>
                <th className="px-4 py-3 font-medium text-right" style={{ color: "#64748B" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t" style={{ borderColor: "#F1F5F9" }} data-testid={`contract-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-2" style={{ color: "#0B2545" }}>
                      {c.title}
                      <ExpiryBadge endDate={c.end_date} status={c.status} />
                    </div>
                    {c.filename && (
                      <a
                        href={contractDownloadUrl(c.id)}
                        className="inline-flex items-center gap-1 mt-1 text-xs hover:underline"
                        style={{ color: "#7A5E12" }}
                        data-testid={`contract-download-${c.id}`}
                      >
                        <Download className="h-3 w-3" /> {c.filename}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3" style={{ color: "#475569" }}>
                    {c.link_type ? (
                      <span className="inline-flex items-center gap-1.5">
                        {c.link_type === "facility" ? <Building2 className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5" />}
                        <span className="text-xs">{c.link_label || c.link_id}</span>
                      </span>
                    ) : <span className="text-xs" style={{ color: "#94A3B8" }}>—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-4 py-3 text-xs" style={{ color: "#475569" }}>
                    {c.start_date || "—"} → {c.end_date || "—"}
                  </td>
                  <td className="px-4 py-3 text-right" style={{ color: "#0B2545" }}>
                    {c.value != null ? `$${Number(c.value).toLocaleString()}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setDialogOpen(true); }}
                              data-testid={`contract-edit-${c.id}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(c.id)}
                              data-testid={`contract-delete-${c.id}`} style={{ color: "#9B1C1C" }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ContractFormDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchAll}
      />
    </div>
  );
}

const StatCard = ({ label, value, accent = "#0B2545" }) => (
  <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}>
    <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#64748B" }}>{label}</div>
    <div className="font-display text-2xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

const Chip = ({ active, label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-xs px-3 py-1.5 rounded-full border transition-colors"
    style={{
      background: active ? "#0B2545" : "#FFFFFF",
      color: active ? "#FFD774" : "#475569",
      borderColor: active ? "#0B2545" : "#E2E8F0",
    }}
  >
    {label}
  </button>
);
